import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi, usePatchAuthApi } from '@/hooks/UseApiCalls';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import { DonationPageConfig } from '../PublicDonation/types';
import styles from './DonationsAdmin.module.css';

interface Props {
  fundId: number;
}

const TOGGLES = [
  { key: 'notifyOnNewDonation', i18n: 'donations.admin.notifications.onNew', hint: 'donations.admin.notifications.onNewHint' },
  { key: 'notifyOnApproved', i18n: 'donations.admin.notifications.onApproved', hint: 'donations.admin.notifications.onApprovedHint' },
  { key: 'notifyOnRejected', i18n: 'donations.admin.notifications.onRejected', hint: 'donations.admin.notifications.onRejectedHint' },
] as const;

type ToggleKey = (typeof TOGGLES)[number]['key'];
type ToggleStatus = 'idle' | 'saving' | 'saved' | 'error';

const NotificationSettings = ({ fundId }: Props) => {
  const { t } = useTranslation();
  const { data: config } = useGetAuthApi<DonationPageConfig>(`donations/page-config/${fundId}`);
  const { mutateAsync: patch } = usePatchAuthApi(`donations/page-config/${fundId}`);

  const [values, setValues] = useState<Record<ToggleKey, boolean>>({
    notifyOnNewDonation: true,
    notifyOnApproved: true,
    notifyOnRejected: true,
  });

  const [statuses, setStatuses] = useState<Record<ToggleKey, ToggleStatus>>({
    notifyOnNewDonation: 'idle',
    notifyOnApproved: 'idle',
    notifyOnRejected: 'idle',
  });

  const timers = useRef<Partial<Record<ToggleKey, ReturnType<typeof setTimeout>>>>({});

  useEffect(() => {
    if (config) {
      setValues({
        notifyOnNewDonation: config.notifyOnNewDonation,
        notifyOnApproved: config.notifyOnApproved,
        notifyOnRejected: config.notifyOnRejected,
      });
    }
  }, [config]);

  const toggle = async (key: ToggleKey, checked: boolean) => {
    const prev = values[key];
    setValues((v) => ({ ...v, [key]: checked }));
    setStatuses((s) => ({ ...s, [key]: 'saving' }));
    clearTimeout(timers.current[key]);
    try {
      await patch({ [key]: checked });
      setStatuses((s) => ({ ...s, [key]: 'saved' }));
    } catch {
      setValues((v) => ({ ...v, [key]: prev }));
      setStatuses((s) => ({ ...s, [key]: 'error' }));
    }
    timers.current[key] = setTimeout(() => setStatuses((s) => ({ ...s, [key]: 'idle' })), 2500);
  };

  const statusIndicator = (key: ToggleKey) => {
    const s = statuses[key];
    if (s === 'idle') return null;
    if (s === 'saving') return <span className={styles.saveSaving}>⟳</span>;
    if (s === 'saved') return <span className={styles.saveSuccess}>✓</span>;
    return <span className={styles.saveError}>✗</span>;
  };

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t('donations.admin.notifications.title')}</h2>
      <p className={styles.panelSubtitle}>{t('donations.admin.notifications.subtitle')}</p>
      <h4 className={styles.toggleListTitle}>{t('donations.admin.notifications.sendWhen')}</h4>
      <div className={styles.toggleList}>
        {TOGGLES.map(({ key, i18n, hint }) => (
          <div key={key} className={styles.toggleRow}>
            <ToggleSwitch
              label={t(i18n)}
              checked={values[key]}
              onChange={(checked) => toggle(key, checked)}
              disabled={statuses[key] === 'saving'}
            />
            <span className={styles.toggleHint}>{t(hint)}</span>
            {statusIndicator(key)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationSettings;
